import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

export type ChannelType = "email" | "webhook" | "in_app";
export type Severity = "info" | "warning" | "critical";
export type DispatchStatus = "pending" | "sent" | "failed" | "suppressed";

export interface NotificationChannel {
  id: string;
  name: string;
  type: ChannelType;
  config: Record<string, unknown>;
  is_active: boolean;
  created_at?: unknown;
  updated_at?: unknown;
}

export interface NotificationRule {
  id: string;
  name: string;
  event_type: string;
  min_severity: Severity;
  channel_id: string;
  is_active: boolean;
  dedup_window_seconds: number;
  created_at?: unknown;
  updated_at?: unknown;
}

export interface DispatchLogEntry {
  id: string;
  rule_id?: string | null;
  channel_id?: string | null;
  event_type: string;
  severity: Severity;
  status: DispatchStatus;
  error?: string | null;
  payload: Record<string, unknown>;
  created_at?: unknown;
  sent_at?: unknown;
}

export interface FeedEntry {
  id: string;
  event_type: string;
  severity: Severity;
  title?: string | null;
  body?: string | null;
  source_alert_id?: string | null;
  read: boolean;
  created_at?: unknown;
}

const CHANNELS_KEY = ["notifications", "channels"] as const;
const RULES_KEY = ["notifications", "rules"] as const;
const LOG_KEY = ["notifications", "log"] as const;
const FEED_KEY = ["notifications", "feed"] as const;
const UNREAD_KEY = ["notifications", "unread"] as const;

// ---------- channels ----------
export function useChannels() {
  return useQuery({
    queryKey: CHANNELS_KEY,
    queryFn: async () => {
      const { data } = await api.get<{
        items: NotificationChannel[]; total: number;
      }>("/api/v1/notifications/channels");
      return data;
    },
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string; type: ChannelType;
      config: Record<string, unknown>;
      is_active?: boolean;
    }) => {
      const { data } = await api.post<NotificationChannel>(
        "/api/v1/notifications/channels", input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANNELS_KEY }),
  });
}

export function useUpdateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      patch: Partial<{
        name: string;
        config: Record<string, unknown>;
        is_active: boolean;
      }>;
    }) => {
      const { data } = await api.patch<NotificationChannel>(
        `/api/v1/notifications/channels/${input.id}`, input.patch,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANNELS_KEY }),
  });
}

export function useDeleteChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/notifications/channels/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANNELS_KEY }),
  });
}

// ---------- rules ----------
export function useRules() {
  return useQuery({
    queryKey: RULES_KEY,
    queryFn: async () => {
      const { data } = await api.get<{
        items: NotificationRule[]; total: number;
      }>("/api/v1/notifications/rules");
      return data;
    },
  });
}

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string; event_type: string; min_severity: Severity;
      channel_id: string; is_active?: boolean;
      dedup_window_seconds?: number;
    }) => {
      const { data } = await api.post<NotificationRule>(
        "/api/v1/notifications/rules", input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES_KEY }),
  });
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      patch: Partial<{
        name: string;
        event_type: string;
        min_severity: Severity;
        channel_id: string;
        is_active: boolean;
        dedup_window_seconds: number;
      }>;
    }) => {
      const { data } = await api.patch<NotificationRule>(
        `/api/v1/notifications/rules/${input.id}`, input.patch,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES_KEY }),
  });
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/notifications/rules/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES_KEY }),
  });
}

// ---------- log ----------
export function useDispatchLog(filters?: {
  status?: DispatchStatus;
  event_type?: string;
  from?: string;
  to?: string;
}) {
  return useQuery({
    queryKey: [...LOG_KEY, filters ?? {}],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.set("status", filters.status);
      if (filters?.event_type) params.set("event_type", filters.event_type);
      if (filters?.from) params.set("from", filters.from);
      if (filters?.to) params.set("to", filters.to);
      const { data } = await api.get<{
        items: DispatchLogEntry[]; total: number;
      }>(`/api/v1/notifications/log?${params.toString()}`);
      return data;
    },
  });
}

// ---------- feed ----------
export function useFeed() {
  return useQuery({
    queryKey: FEED_KEY,
    queryFn: async () => {
      const { data } = await api.get<{
        items: FeedEntry[]; unread_count: number; total: number;
      }>("/api/v1/notifications/feed");
      return data;
    },
    refetchInterval: 30_000,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: UNREAD_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ unread_count: number }>(
        "/api/v1/notifications/feed/unread-count",
      );
      return data.unread_count;
    },
    refetchInterval: 30_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/api/v1/notifications/feed/${id}/read`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FEED_KEY });
      qc.invalidateQueries({ queryKey: UNREAD_KEY });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post("/api/v1/notifications/feed/read-all");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FEED_KEY });
      qc.invalidateQueries({ queryKey: UNREAD_KEY });
    },
  });
}
